import debounce from 'lodash-es/debounce';
import identity from 'lodash-es/identity';
import noop from 'lodash-es/noop';

/**
 * Výčet možných stavů načítání jedné položky.
 *
 * @enum {string}
 */
const LoadState = {
    LOADING: 'LOADING',
    LOADED: 'LOADED',
    ERROR: 'ERROR',
    CANCELED: 'CANCELED',
};

const getItemData = item => item.data;

/**
 * Utilita pro asynchronní načítání dle dle příslušného klíče.
 * - Načtená data jsou dle klíče interně cachována.
 * - Umí notifikovat o změně průběhu načítání.
 * - Umí vracet načtená data a stav průběhu načítání.
 */
export default class CachingLoader {

    /**
     * Konstruktor.
     *
     * @param {function} itemLoader - callback pro synchronní či asynchronní načtení dat jedné položky dle jejího klíče
     * @param {Object} [options] - volitelné parametry
     * @param {function} [options.resultDataExtractor] - callback pro získání dat položky z výsledku jejího načtení
     * @param {function} [options.onLoadStateChange] - callback volaný při změně stavu načítání položek
     * @param {number} [options.loadDelay] - spoždění načítání (debounce)
     */
    constructor(itemLoader, options = {}) {
        this.items = new Map();
        this.itemLoader = itemLoader;
        this.resultDataExtractor = options.resultDataExtractor || identity;
        this.onLoadStateChange = options.onLoadStateChange || noop;

        const {loadDelay} = options;
        if (loadDelay != null) {
            this.loadItems = debounce(this.loadItems.bind(this), loadDelay);
        }
    }

    /**
     * Spustí načítání položek s příslušnými klíči.
     * - Průběh načítání každé položky je uložen dle klíče do interní cache
     * - Pro položky nalezené dle klíče v cache se použije existující stav načítání.
     * - Z cache jsou odstraněny položky, na jejichž klíče se nedotazujeme.
     *
     * @param {Array<*>} keys - pole klíčů položek
     * @param {Object} [options] - volitelé parametry
     * @param {boolean} [options.reloadErrors=false] -  příznak, zda vynutit opětovné načtení položek v cache,
     *                                                  u nichž skončilo přechozí načítání chybou
     */
    loadItems(keys, options = {}) {
        const {reloadErrors = false} = options;
        let loadStateChanged = false;

        // Odstraníme nepotřebné položky z cache
        for (const key of this.items.keys()) {
            if (keys.indexOf(key) <= 0) {
                this._deleteItem(key);
                loadStateChanged = true;
            }
        }

        // Přidáme nové položky do cache
        for (const key of keys) {
            if (!this.items.has(key)) {
                this.items.set(key, {});
            }
        }

        // Spustíme načítání u některých položek
        for (const [key, item] of this.items) {
            const {state} = item;
            if (state == null || (reloadErrors && state === LoadState.ERROR)) {
                this._loadItem(key, item);
                loadStateChanged = true;
            }
        }

        if (loadStateChanged) {
            this.onLoadStateChange();
        }
    }

    /**
     * Spustí načítání jedné položky.
     *
     * @param {*} key - klíč položky
     * @param {Object} item - záznam položky
     * @private
     */
    _loadItem(key, item) {
        item.state = LoadState.LOADING;

        try {
            Promise.resolve(this.itemLoader(key))
                .then(result => this._onItemLoadSuccess(item, result))
                .catch(error => this._onItemLoadError(item, error));
        } catch (error) {
            this._onItemLoadError(item, error);
        }
    }

    /**
     * Callback volaný při úspěšném načtení položky.
     *
     * @param {*} item - záznam položky
     * @param {*} result - výsledek načtení
     * @private
     */
    _onItemLoadSuccess(item, result) {
        if (item.state === LoadState.CANCELED) {
            return;
        }

        try {
            item.state = LoadState.LOADED;
            item.data = this.resultDataExtractor(result);
        } catch (error) {
            item.state = LoadState.ERROR;
            item.error = error;
        }

        this.onLoadStateChange();
    }

    /**
     * Callback volaný při chybě načtení položky.
     *
     * @param {*} item - záznam položky
     * @param {*} error - chyba načtení
     * @private
     */
    _onItemLoadError(item, error) {
        if (item.state === LoadState.CANCELED) {
            return;
        }

        item.state = LoadState.ERROR;
        item.error = error;

        this.onLoadStateChange();
    }

    /**
     * Smaže záznam položky z cache.
     *
     * @param {*} key - klíč položky
     * @private
     */
    _deleteItem(key) {
        const item = this.items.get(key);
        if (item) {
            item.state = LoadState.CANCELED;
            this.items.delete(key);
        }
    }

    /**
     * Otestuje, zda právě probíhá načítání.
     *
     * @returns {boolean} true, pokud se načítá alespoň jedna položka, jinak false
     */
    isLoading() {
        return this._existItemsWithState(LoadState.LOADING);
    }

    /**
     * Vrátí iterátor, který projde data načtených položek.
     *
     * @returns {IterableIterator<*>} iterátor
     */
    getLoadedData() {
        return this._getItemsWithState(LoadState.LOADED, getItemData);
    }

    /**
     * Otestuje, zda exsituje alespoň jedna položka v daném stavu.
     *
     * @param {LoadState} state - stav načítání
     * @returns {boolean} true, pokud existuje alespoň jedna položka v daném stavu, jinak false
     * @private
     */
    _existItemsWithState(state) {
        return !this._getItemsWithState(state).next().done;
    }

    /**
     * Vrátí iterátor nad položkami v daném stavu.
     *
     * @param {LoadState} state - stav načítání
     * @param {function} mapFunction - mapovací funkce pro každou z položek
     * @returns {IterableIterator<*>} iterátor
     * @private
     */
    *_getItemsWithState(state, mapFunction = identity) {
        for (const item of this.items.values()) {
            if (item.state === state) {
                yield mapFunction(item);
            }
        }
    }

}
